import classNames         from 'classnames';
import * as React         from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory }     from 'react-router-dom';

import useEventListener from '../../../../common/hooks/useEventListener';

import CompareAddProductIcon from '../../../components/icons/CompareAddProductIcon';
import { useCommon }         from '../../../providers/common';
import { useModal }          from '../../../providers/modal';

import CompareSectionRows             from './CompareSectionRows';
import CompareStickyProductCard       from './CompareStickyProductCard';
import CompareTableItem               from './CompareTableItem';
import CompareTableTitle              from './CompareTableTitle';

const CompareTable = function(props) {
  const history = useHistory();
  const { t }   = useTranslation();

  const {
          compareOffers,
          handleCatalogResetOnClick,
          isInterior,
          setCurrentOffer,
        } = props;

  const common = useCommon();
  const modal  = useModal();

  const containerRef = React.useRef(null);

  const [isDragging, setIsDragging]         = React.useState(false);
  const [isIntersecting, setIsIntersecting] = React.useState(false);
  const [screenWidth, setScreenWidth]       = React.useState(0);
  const [scrollLeft, setScrollLeft]         = React.useState(0);
  const [scrollPosition, setScrollPosition] = React.useState(0);
  const [startX, setStartX]                 = React.useState(0);

  const goToCatalog     = React.useCallback(() => {
    const catalogUrl = common.getUrlByTemplate(isInterior ? 'mezhkomnatnye-dveri' : 'vhodnye-dveri');

    handleCatalogResetOnClick(catalogUrl);

    history.push(catalogUrl);
  }, [handleCatalogResetOnClick, history, isInterior]);
  const showInfo        = React.useCallback((label, content, top = '', left = '') => {
    let data = content;

    try {
      data = JSON.parse(content);
    } catch (error) {
      throw new Error(error);
    }

    modal.showModal(label, data, false, 'more_info', top, left);
  }, []);
  const handleMouseDown = React.useCallback((event) => {
    setIsDragging(true);
    setStartX(event.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  }, []);
  const handleMouseMove = React.useCallback((event) => {
    if (!isDragging) {
      return;
    }

    event.preventDefault();

    const x = event.pageX - containerRef.current.offsetLeft;

    containerRef.current.scrollLeft = scrollLeft - (x - startX);
  }, [isDragging, startX, scrollLeft]);
  const handleResize    = React.useCallback(() => {
    setScreenWidth(window.screen.width);
  }, []);
  const handleHorizontalScroll = (event) => {
    const target = event.target;

    if (target) {
      setScrollPosition(target.scrollLeft);
    }
  };

  useEventListener('resize', handleResize);

  React.useEffect(() => {
    handleResize();
  }, []);
  React.useEffect(() => {
    const doorElement = document.querySelector('.table_head');
    // Capture the container node now: by the time the cleanup below runs (e.g. on unmount when
    // navigating away from /compare), React has already nulled containerRef.current, so reading
    // the ref inside the cleanup throws ("unobserve" expects an Element, gets null) and crashes
    // the app (uncaught error during navigation away from the page).
    const container    = containerRef.current;

    if (!doorElement || !container) {
      return;
    }

    const observer          = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    }, {
      root:      null,
      threshold: 0,
    });
    const containerObserver = new IntersectionObserver(([entry]) => {
      setIsIntersecting(!entry.isIntersecting);
    }, {
      root:      null,
      threshold: 0,
    });

    observer.observe(doorElement);
    containerObserver.observe(container);

    return () => {
      observer.unobserve(doorElement);
      containerObserver.unobserve(container);
    };
  }, [compareOffers]);

  if (!compareOffers.length) {
    return null;
  }

  return (
    <div
      className={classNames('compare_table_container', {
        'grabbing': isDragging && compareOffers.length > 3,
        'grab':     compareOffers.length > 3,
      })}
      onMouseDown={handleMouseDown}
      onMouseLeave={() => setIsDragging(false)}
      onMouseUp={() => setIsDragging(false)}
      onMouseMove={handleMouseMove}
      onScroll={handleHorizontalScroll}
      ref={containerRef}
    >
      <table>
        <thead className={classNames('table_head')}>
        <tr className={'table_row'}>
          <th className={'white_space'} />
          {compareOffers.map((compareOffer, index) => (
            <CompareTableItem
              key={index}
              isInterior={isInterior}
              offer={{ [compareOffer.common.offerId]: compareOffer }}
              setCurrentOffer={setCurrentOffer}
              screenWidth={screenWidth}
            />
          ))}
          <th>
            <div
              className={'add_product'}
              onClick={goToCatalog}
            >
              <CompareAddProductIcon />
              <p>{t('client_add_product')}</p>
            </div>
          </th>
        </tr>
        </thead>
        {(compareOffers[0]?.sections || []).map((section) => (
          <React.Fragment key={section.alias}>
            <CompareTableTitle
              compareOffers={compareOffers}
              label={section.label}
            />
            <CompareSectionRows
              compareOffers={compareOffers}
              section={section}
              showInfo={showInfo}
              screenWidth={screenWidth}
            />
          </React.Fragment>
        ))}
      </table>
      <CompareStickyProductCard
        compareOffers={compareOffers}
        isIntersecting={isIntersecting}
        goToCatalog={goToCatalog}
        scroll={scrollPosition}
      />
    </div>
  );
};

export default CompareTable;